import React, { useEffect, useState } from 'react';
import { ClampText } from '@/components/ui';
import { Button, DataTable, EmptyValue } from '../../../components/ui';
import type { DataTableColumn } from '../../../components/ui';
import { binsToShow, narrowsBins } from '../filters';
import type { BinFilters } from '../filters';
import type { Bin, Site } from '../types';
import { formatQuantity } from '../utils/adjustInventory';
import { locationKindLabel } from '../utils/siteDrillThrough';
import BinStatusBadge from './BinStatusBadge';
import OwnerPill from './OwnerPill';

interface BinRow {
  bin: Bin;
  locationName: string;
  locationType: string;
  /** First bin of its location: the location name is shown once per group. */
  first: boolean;
}

const COLUMNS: DataTableColumn<BinRow>[] = [
  {
    key: 'location',
    header: 'Location',
    mobile: 'subtitle',
    className: 'tw-min-w-[11rem]',
    cell: ({ locationName, locationType, first }) =>
      first ? (
        <div>
          <p className="tw-mb-0 tw-font-medium tw-text-content">{locationName}</p>
          <p className="tw-mb-0 tw-text-xs tw-text-content-muted">{locationKindLabel(locationType)}</p>
        </div>
      ) : null,
  },
  {
    key: 'bin',
    header: 'Bin',
    mobile: 'title',
    className: 'tw-whitespace-nowrap',
    cell: ({ bin }) => <span className="tw-font-medium tw-text-content">{bin.name}</span>,
  },
  {
    key: 'material',
    header: 'Material',
    className: 'tw-min-w-[14rem]',
    cell: ({ bin }) =>
      bin.material ? (
        <ClampText title={bin.material}>
          {bin.material}
        </ClampText>
      ) : (
        <span className="tw-text-content-muted">No material assigned</span>
      ),
  },
  {
    key: 'onHand',
    header: 'On Hand',
    mobileLabel: 'On Hand',
    align: 'right',
    className: 'tw-whitespace-nowrap tw-tabular-nums',
    cell: ({ bin }) =>
      bin.materialId ? (
        <span className={bin.status === 'negative' || bin.status === 'below-min' ? 'tw-font-semibold tw-text-danger' : 'tw-text-content'}>
          {formatQuantity(bin.quantity, bin.unitOfMeasure)}
        </span>
      ) : (
        <EmptyValue />
      ),
  },
  {
    key: 'band',
    header: 'Min / Max',
    mobileLabel: 'Min / Max',
    align: 'right',
    className: 'tw-whitespace-nowrap tw-tabular-nums tw-text-content-secondary',
    cell: ({ bin }) =>
      bin.minQuantity === null && bin.maxQuantity === null ? (
        <EmptyValue />
      ) : (
        <>
          {bin.minQuantity === null ? '–' : formatQuantity(bin.minQuantity, bin.unitOfMeasure)}
          {' / '}
          {bin.maxQuantity === null ? '–' : formatQuantity(bin.maxQuantity, bin.unitOfMeasure)}
        </>
      ),
  },
  {
    key: 'status',
    header: 'Status',
    mobile: 'trailing',
    cell: ({ bin }) => <BinStatusBadge status={bin.status} />,
  },
  {
    key: 'owner',
    header: 'Owner',
    mobileLabel: 'Owner',
    cell: ({ bin }) => <OwnerPill owner={bin.owner} />,
  },
];

interface SiteBinsPanelProps {
  site: Site;
  /** The list's material and needs-action filters: when set, show just the bins they matched. */
  filters: BinFilters;
  onOpen: () => void;
}

/** The bins under one site, opened from the chevron on its row. */
const SiteBinsPanel: React.FC<SiteBinsPanelProps> = ({ site, filters, onOpen }) => {
  const [showAll, setShowAll] = useState(false);
  // New filters narrow again, even after "Show all".
  const filterKey = `${filters.materialIds.join(',')}|${filters.attention}`;
  useEffect(() => setShowAll(false), [filterKey]);
  const total = site.locations.reduce((sum, location) => sum + location.bins.length, 0);

  const rows: BinRow[] = site.locations.flatMap((location) => {
    const bins = showAll ? location.bins : binsToShow(location.bins, filters);
    return bins.map((bin, index) => ({ bin, locationName: location.name, locationType: location.type, first: index === 0 }));
  });
  const narrowed = !showAll && narrowsBins(filters) && rows.length < total;

  if (total === 0) {
    return <p className="tw-mb-0 tw-px-4 tw-py-3 tw-text-sm tw-text-content-muted">No locations or bins recorded at this site yet.</p>;
  }

  return (
    <div className="tw-space-y-2 tw-px-3 tw-py-3">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-2">
        <p className="tw-mb-0 tw-text-xs tw-text-content-muted">
          {narrowed ? `Showing ${rows.length} of ${total} bins that match your filters.` : `${total} ${total === 1 ? 'bin' : 'bins'} across ${site.locations.length} ${site.locations.length === 1 ? 'location' : 'locations'}.`}
          {narrowed ? (
            <Button variant="link" size="sm" className="tw-ml-1 tw-p-0 tw-align-baseline" onClick={() => setShowAll(true)}>
              Show all
            </Button>
          ) : null}
        </p>
        <Button size="sm" variant="outline-primary" onClick={onOpen}>
          Open site
          <i className="bi bi-box-arrow-up-right" aria-hidden="true" />
        </Button>
      </div>
      <div className="tw-overflow-hidden tw-rounded-lg tw-border tw-border-line tw-bg-surface">
        <DataTable
          className="[&_table]:tw-min-w-[52rem]"
          columns={COLUMNS}
          data={rows}
          rowKey={(row) => row.bin.id}
          size="sm"
          emptyMessage="No bins here match your filters."
        />
      </div>
    </div>
  );
};

export default SiteBinsPanel;
